import { getAddress, isAddress } from "viem";
import type { Hex } from "./chains";

export type LiveConfig =
  | { status: "demo" }
  | { status: "error"; field: string; message: string }
  | {
      status: "live";
      factoryAddress: Hex;
      expectedHash: Hex;
      deploymentBlock: bigint;
      deploymentTx: Hex | null;
      chainId: number;
    };

export interface LiveConfigInput {
  /** Raw factory address env (VITE_TESTNET_FACTORY_ADDRESS), or null/empty. */
  factoryAddress: string | null;
  /** Pinned expected runtime hash (from canonical config). */
  expectedHash: string | null;
  /** Parsed deployment block (null if unset OR malformed). */
  deploymentBlock: bigint | null;
  /** Raw deployment-block env, used only to distinguish missing vs malformed. */
  rawDeploymentBlock?: string | undefined;
  /** Optional deployment tx env. */
  deploymentTx?: string | null;
  chainId: number;
}

const HEX32 = /^0x[0-9a-fA-F]{64}$/;
const REQUIRED_CHAIN_ID = 46630;

/**
 * Resolve and validate live-factory configuration. Never casts arbitrary strings
 * to Hex. Returns `demo` when no factory is set, a typed `error` (with the exact
 * failing field) when live config is malformed/incomplete, or a fully-validated
 * `live` config. Callers must NOT instantiate the real client on `error`.
 */
export function resolveLiveConfig(input: LiveConfigInput): LiveConfig {
  const addr = input.factoryAddress?.trim();
  if (!addr) return { status: "demo" };

  if (!isAddress(addr)) {
    return { status: "error", field: "VITE_TESTNET_FACTORY_ADDRESS", message: "Not a valid EVM address." };
  }
  const factoryAddress = getAddress(addr) as Hex;

  if (!input.expectedHash || !HEX32.test(input.expectedHash)) {
    return { status: "error", field: "expectedFactoryBytecodeHash", message: "Expected runtime hash is not a 32-byte hex value." };
  }
  const expectedHash = input.expectedHash as Hex;

  if (input.chainId !== REQUIRED_CHAIN_ID) {
    return { status: "error", field: "chainId", message: `Chain id must be ${REQUIRED_CHAIN_ID}.` };
  }

  if (input.deploymentBlock === null) {
    const missing = input.rawDeploymentBlock === undefined || input.rawDeploymentBlock.trim() === "";
    return {
      status: "error",
      field: "VITE_TESTNET_FACTORY_DEPLOYMENT_BLOCK",
      message: missing
        ? "Required when a factory address is set (no fallback to block 0)."
        : "Malformed — must be a non-negative integer.",
    };
  }
  if (input.deploymentBlock < 0n) {
    return { status: "error", field: "VITE_TESTNET_FACTORY_DEPLOYMENT_BLOCK", message: "Must be non-negative." };
  }

  let deploymentTx: Hex | null = null;
  if (input.deploymentTx && input.deploymentTx.trim() !== "") {
    if (!HEX32.test(input.deploymentTx.trim())) {
      return { status: "error", field: "VITE_TESTNET_FACTORY_DEPLOYMENT_TX", message: "Not a valid 32-byte transaction hash." };
    }
    deploymentTx = input.deploymentTx.trim() as Hex;
  }

  return {
    status: "live",
    factoryAddress,
    expectedHash,
    deploymentBlock: input.deploymentBlock,
    deploymentTx,
    chainId: input.chainId,
  };
}
